/**
 * HTML and CSS preprocessing utilities for safe rendering of AI-generated content
 * 
 * This module provides functions to transform potentially problematic HTML content
 * by rewriting global CSS selectors and replacing document-level HTML tags.
 */

/**
 * Rewrite CSS selectors to be scoped under a specific namespace
 * Transforms global selectors like 'body', 'html', '*' to be scoped
 */
export function rewriteCSSSelectors(css: string, namespace: string): string {
  if (!css || !namespace) return css;

  return css
    // Handle body selector - transform to scoped version
    .replace(/\bbody\b(?=\s*[{,])/g, `.${namespace}-body`)
    
    // Handle html selector - transform to scoped version  
    .replace(/\bhtml\b(?=\s*[{,])/g, `.${namespace}-html`)
    
    // Handle universal selector - scope it
    .replace(/(?:^|[,\s])\*(?=\s*[{,])/g, ` .${namespace} *`)
    
    // Handle other potentially problematic selectors
    .replace(/\b:root\b/g, `.${namespace}`)
    
    // Clean up any double spaces
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract and process CSS from <style> tags in HTML
 */
export function processStyleTags(html: string, namespace: string): string {
  return html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, cssContent) => {
    const processedCSS = rewriteCSSSelectors(cssContent, namespace);
    return `<style>${processedCSS}</style>`;
  });
}

/**
 * Transform HTML document tags to div elements
 * Replaces <html>, <body>, <head> tags with <div> equivalents
 */
export function transformDocumentTags(html: string, namespace: string): string {
  return html
    // Replace opening html tag with div
    .replace(/<html[^>]*>/gi, `<div class="${namespace}-html">`)
    
    // Replace closing html tag
    .replace(/<\/html>/gi, '</div>')
    
    // Replace opening body tag with div
    .replace(/<body[^>]*>/gi, `<div class="${namespace}-body">`)
    
    // Replace closing body tag
    .replace(/<\/body>/gi, '</div>')
    
    // Remove head tags and their content (not needed for display)
    .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
    
    // Remove DOCTYPE declarations
    .replace(/<!DOCTYPE[^>]*>/gi, '')
    
    // Remove meta tags
    .replace(/<meta[^>]*\/?>/gi, '')
    
    // Remove title tags
    .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '');
}

/**
 * Process inline styles to remove or scope problematic CSS properties
 */
export function processInlineStyles(html: string, namespace: string): string {
  return html.replace(/style\s*=\s*["']([^"']*)["']/gi, (match, styleContent) => {
    // Remove or transform problematic inline styles
    const processedStyle = styleContent
      // Remove width properties that might affect layout
      .replace(/\b(min-)?width\s*:\s*[^;]+;?/gi, '')
      
      // Remove margin properties on body-like elements
      .replace(/\bmargin\s*:\s*[^;]+;?/gi, '')
      
      // Remove position properties that might break layout
      .replace(/\bposition\s*:\s*(fixed|absolute)[^;]*;?/gi, '')
      
      // Clean up empty or trailing semicolons
      .replace(/;+/g, ';')
      .replace(/^;|;$/g, '')
      .trim();
    
    return processedStyle ? `style="${processedStyle}"` : '';
  });
}

/**
 * Main preprocessing function that applies all transformations
 */
export function preprocessHTMLContent(html: string, namespace: string = 'report-preview'): string {
  if (!html) return '';

  let processedHTML = html;

  // Step 1: Transform document-level tags
  processedHTML = transformDocumentTags(processedHTML, namespace);

  // Step 2: Process CSS in style tags
  processedHTML = processStyleTags(processedHTML, namespace);

  // Step 3: Process inline styles
  processedHTML = processInlineStyles(processedHTML, namespace);

  // Step 4: Wrap the entire content in a scoped container
  processedHTML = `<div class="${namespace}">${processedHTML}</div>`;

  return processedHTML;
}

/**
 * Sanitize HTML content by removing potentially dangerous elements
 */
export function sanitizeHTML(html: string): string {
  return html
    // Remove script tags
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    
    // Remove iframe tags
    .replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi, '')
    
    // Remove form elements that might cause issues
    .replace(/<(form|input|textarea|select|button)[^>]*>[\s\S]*?<\/\1>/gi, '')
    
    // Remove link tags (external stylesheets)
    .replace(/<link[^>]*>/gi, '')
    
    // Remove object and embed tags
    .replace(/<(object|embed)[^>]*>[\s\S]*?<\/\1>/gi, '');
}

/**
 * Complete preprocessing pipeline for AI-generated HTML content
 */
export function processAIGeneratedHTML(html: string): string {
  if (!html) return '';

  const namespace = 'ai-report-content';
  
  // Step 1: Sanitize dangerous content
  let processedHTML = sanitizeHTML(html);
  
  // Step 2: Apply CSS and HTML transformations
  processedHTML = preprocessHTMLContent(processedHTML, namespace);
  
  return processedHTML;
}
